import React, { useState, useCallback, useMemo } from 'react'
import {
  Button,
  Grid,
  Typography,
  Box,
  useTheme,
  Container,
  styled,
} from '@mui/material'
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const FacultyList = ({
  data = [],
  baseImagePath = '',
  placeholderImagePath = '',
  title = 'Our Faculty',
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [hoveredFaculty, setHoveredFaculty] = useState(null)

  const TitleBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    position: 'relative',
    marginBottom: theme.spacing(2),
  }))

  const handleViewProfile = useCallback(
    (index) => {
      const faculty = data[index]
      if (faculty?.pathurl) navigate(`/${faculty.pathurl}`)
    },
    [data, navigate]
  )

  const handleMouseEnter = useCallback((index) => {
    setHoveredFaculty(index)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoveredFaculty(null)
  }, [])

  const facultyNameStyles = useMemo(
    () => ({
      fontWeight: 500,
      color: theme.palette.text.primary,
      fontSize: {
        xs: '0.9rem',
        sm: '1rem',
        md: '1.05rem',
        lg: '1.1rem',
        xl: '1.15rem',
      },
      lineHeight: 1.35,
      mb: 0.5,
    }),
    [theme]
  )

  const designationStyles = useMemo(
    () => ({
      color: theme.palette.text.secondary,
      fontWeight: 400,
      fontSize: {
        xs: '0.75rem',
        sm: '0.8rem',
        md: '0.85rem',
        lg: '0.9rem',
        xl: '0.95rem',
      },
      lineHeight: 1.2,
      letterSpacing: '0.02em',
    }),
    [theme]
  )

  const buttonStyles = useMemo(
    () => ({
      transform: hoveredFaculty !== null ? 'translateY(0)' : 'translateY(70px)',
      opacity: hoveredFaculty !== null ? 1 : 0,
      transition: 'all 0.3s ease 0.1s',
      color: 'white',
      backgroundColor: 'transparent',
      fontWeight: 600,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'white',
        boxShadow: 'none',
      },
    }),
    [hoveredFaculty]
  )

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        {/* Title */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <TitleBox>
              <Typography
                variant="h4"
                component="h5"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 'bold',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                {title}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '70px',
                    height: '4px',
                    backgroundColor: 'background.secondaryBackground',
                    borderRadius: 1,
                  }}
                />
              </Typography>
            </TitleBox>
          </Grid>
        </Grid>

        {/* Faculty Cards */}
        <Grid container spacing={4}>
          {data.map((faculty, index) => {
            const hasProfile = Boolean(faculty.pathurl)
            const isHovered = hoveredFaculty === index

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={`${faculty.Name}-${index}`}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: hasProfile ? 'pointer' : 'not-allowed',
                    maxWidth: '240px',
                    mx: 'auto',
                  }}
                  onClick={() => hasProfile && handleViewProfile(index)}
                >
                  {/* Image Box */}
                  <Box
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      position: 'relative',
                      width: { xs: '100%', sm: '90%', md: '180px' }, // Reduced from 190px
                      aspectRatio: '1 / 1.1',
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        faculty.image
                          ? baseImagePath + faculty.image
                          : placeholderImagePath
                      }
                      alt={faculty.Name}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'filter 0.4s ease',
                      }}
                      onError={(e) => {
                        e.target.src = placeholderImagePath
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: isHovered ? '100%' : '0%',
                        background: isHovered
                          ? `linear-gradient(to top, ${theme.palette.primary.dark}, transparent)`
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        pb: 2,
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                      }}
                    >
                      <Button
                        variant="contained"
                        endIcon={
                          <ArrowForwardIcon
                            sx={{
                              transform: 'rotate(-45deg)',
                              fontSize: '1.2rem',
                            }}
                          />
                        }
                        sx={buttonStyles}
                        disabled={!hasProfile}
                      >
                        View Profile
                      </Button>
                    </Box>
                  </Box>

                  {/* Name & Designation */}
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '220px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        ...facultyNameStyles,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                        px: 1,
                      }}
                    >
                      {faculty.Name}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        ...designationStyles,
                        maxWidth: '100%',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {faculty.designation}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )
          })}
        </Grid>
      </Box>
    </Container>
  )
}

export default React.memo(FacultyList)
